// Assessment-test authoring schemas: the CMS-input subset plus the definition
// and request schemas used by the CMS endpoints (openapi extensions and
// SQLTable/results schemas omitted — not needed for authoring).
import { z } from 'zod';
const IdContentPair = z.object({
    id: z.string(),
    content: z.string(),
});
// Authoring-side rubric (model answer, depth-tagged facets, etc.). Optional —
// not every assessment ships with it. Loose record so existing free-form shapes
// (engineering: modelAnswer/facets/internalDoc, productivity: expectedAnswer/
// answerQuality) flow through validation unchanged.
const InternalDocSchema = z
    .record(z.string(), z.unknown())
    .nullable()
    .optional();
export var QuestionType;
(function (QuestionType) {
    QuestionType["TEXT_INPUT"] = "text-input";
    QuestionType["RANKING"] = "ranking";
    QuestionType["MULTIPLE_CHOICE"] = "multiple-choice";
    QuestionType["MATCHING"] = "matching";
    QuestionType["FILL_IN_THE_GAP"] = "fill-in-the-gap";
    QuestionType["PY_PLAYGROUND"] = "py-playground";
    QuestionType["LLM_PLAYGROUND"] = "llm-playground";
    QuestionType["MULTIPLE_RATING"] = "multiple-rating";
    QuestionType["PY_EDITOR"] = "py-editor";
    QuestionType["LLM_EDITOR"] = "llm-editor";
    QuestionType["TEXT_TEMPLATE"] = "text-template";
    QuestionType["MATRIX_RANKING"] = "matrix-ranking";
    QuestionType["TEXT"] = "text";
})(QuestionType || (QuestionType = {}));
const TextInputSectionSchema = z.object({
    type: z.literal('text-input'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    placeholder: z.string().nullable(),
    _internalDoc: InternalDocSchema,
});
const RankingSectionSchema = z.object({
    type: z.literal('ranking'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    options: z.array(IdContentPair),
    _internalDoc: InternalDocSchema,
});
const MultipleChoiceSectionSchema = z.object({
    type: z.literal('multiple-choice'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    isSingleChoice: z.boolean(),
    options: z.array(IdContentPair),
    _internalDoc: InternalDocSchema,
});
const MatchingSectionSchema = z.object({
    type: z.literal('matching'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    leftItems: z.array(IdContentPair),
    rightItems: z.array(IdContentPair),
    _internalDoc: InternalDocSchema,
});
const FillInTheGapSectionSchema = z.object({
    type: z.literal('fill-in-the-gap'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    template: z.string(),
    blanks: z.array(z.object({ id: z.string(), placeholder: z.string() })),
    _internalDoc: InternalDocSchema,
});
const PyPlaygroundSectionSchema = z.object({
    type: z.literal('py-playground'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    setupCode: z.string(),
    startingCode: z.string(),
    _internalDoc: InternalDocSchema,
});
const LlmPlaygroundSectionSchema = z.object({
    type: z.literal('llm-playground'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    initialPrompt: z.string(),
    _internalDoc: InternalDocSchema,
});
const MultipleRatingSectionSchema = z.object({
    type: z.literal('multiple-rating'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    questions: z.array(z.object({
        id: z.string(),
        content: z.string(),
        options: z.array(z.string()),
    })),
    _internalDoc: InternalDocSchema,
});
const PyEditorSectionSchema = z.object({
    type: z.literal('py-editor'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    startingCode: z.string(),
    _internalDoc: InternalDocSchema,
});
const LlmEditorSectionSchema = z.object({
    type: z.literal('llm-editor'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    initialPrompt: z.string(),
    _internalDoc: InternalDocSchema,
});
const TextTemplateSectionSchema = z.object({
    type: z.literal('text-template'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    template: z.string(),
    blanks: z.array(z.object({ id: z.string(), placeholder: z.string() })),
    _internalDoc: InternalDocSchema,
});
const MatrixRankingSectionSchema = z.object({
    type: z.literal('matrix-ranking'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    items: z.array(IdContentPair),
    horizontalAxisLabel: z.string(),
    verticalAxisLabel: z.string(),
    annotationPrompt: z.string().nullable().optional(),
    quadrantLabels: z
        .object({
        topLeft: z.string(),
        topRight: z.string(),
        bottomLeft: z.string(),
        bottomRight: z.string(),
    })
        .optional(),
    _internalDoc: InternalDocSchema,
});
const TextSectionSchema = z.object({
    type: z.literal('text'),
    id: z.string(),
    content: z.string(),
    followUpContent: z.string().nullable().optional(),
    followUpPlaceholder: z.string().nullable().optional(),
    _internalDoc: InternalDocSchema,
});
export const AssessmentTestSectionSchema = z.discriminatedUnion('type', [
    TextInputSectionSchema,
    RankingSectionSchema,
    MultipleChoiceSectionSchema,
    MatchingSectionSchema,
    FillInTheGapSectionSchema,
    PyPlaygroundSectionSchema,
    LlmPlaygroundSectionSchema,
    MultipleRatingSectionSchema,
    PyEditorSectionSchema,
    LlmEditorSectionSchema,
    TextTemplateSectionSchema,
    MatrixRankingSectionSchema,
    TextSectionSchema,
]);
export const AssessmentTestStepSchema = z.object({
    id: z.string(),
    title: z.string(),
    label: z.string(),
    introduction: z.string().nullable(),
    sections: z.array(AssessmentTestSectionSchema),
    objectives: z.array(z.object({
        id: z.string(),
        description: z.string(),
        facets: z.array(z.object({
            questions: z.array(z.string()),
            description: z.string(),
        })),
    })),
});
export const AssessmentTestDimensionSchema = z.object({
    id: z.string(),
    name: z.string(),
    label: z.string(),
    iconUrl: z.string(),
    // Plain-language definition of the skill, shown to learners and managers
    // wherever the skill needs explaining. Optional because older test content
    // predates it (those fall back to the self-assessment question copy).
    description: z.string().optional(),
});
export const AssessmentTestDefinitionSchema = z.object({
    name: z.string(),
    slug: z.string(),
    steps: z.array(AssessmentTestStepSchema),
    dimensions: z.array(AssessmentTestDimensionSchema),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateCmsAssessmentTestRequestSchema = z.object({
    name: z.string().min(1),
    slug: z.string().min(1),
    steps: z.array(AssessmentTestStepSchema),
    dimensions: z.array(AssessmentTestDimensionSchema),
});
export const UpdateCmsAssessmentTestRequestSchema = z.object({
    name: z.string().min(1),
    steps: z.array(AssessmentTestStepSchema),
    dimensions: z.array(AssessmentTestDimensionSchema),
});
